import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import TermsAcceptance from "../components/TermsAcceptance";
import BookingCalendar from "../components/BookingCalendar";
import ApiService from "../services/ApiService";
import logo from "../assets/logo.png";
import "./UserDashboard.css";

const UserDashboard = () => {
  const { user: currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userCredits, setUserCredits] = useState(0);
  const [payPerUseCredits, setPayPerUseCredits] = useState(0);
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);

  // Check terms acceptance when component mounts
  useEffect(() => {
    const checkUserStatus = async () => {
      if (!currentUser) {
        navigate("/auth");
        return;
      }

      try {
        // Get user profile to check terms acceptance status and credits
        const response = await ApiService.getUserProfile(currentUser.uid);
        const profileData = response.data || response;
        if (!profileData.termsAndConditionsAccepted) {
          setShowTermsModal(true);
        } else {
          // Terms are accepted, check credits
          const credits = profileData.credits || 0;
          const payPerUse = profileData.payPerUseCredits || 0;
          setUserCredits(credits);
          setPayPerUseCredits(payPerUse);

          // Fetch active subscriptions
          try {
            const subscriptionsResponse =
              await ApiService.getActiveUserSubscriptions();
            setActiveSubscriptions(subscriptionsResponse.data || []);
          } catch (subscriptionError) {
            console.warn("Could not fetch subscriptions:", subscriptionError);
          } // Check if user is coming from booking confirmation (via navigation state)
          const fromBookingConfirmation =
            location.state?.fromBookingConfirmation;

          // Only redirect to purchase flow if no credits AND not coming from booking confirmation AND not explicitly trying to access dashboard
          // Allow users with credits to stay on dashboard and access purchase flow via "Purchase More Credits" button
          if (
            !fromBookingConfirmation &&
            credits === 0 &&
            payPerUse === 0 &&
            !location.state?.allowDashboard
          ) {
            navigate("/integrated-purchase-flow");
          }
          // If has any credits or coming from booking confirmation, stay on dashboard and show booking interface
        }
      } catch (error) {
        console.error("Error checking user status:", error);
        // On error, show terms modal as a fallback
        setShowTermsModal(true);
      } finally {
        setLoading(false);
      }
    };

    checkUserStatus();
  }, [currentUser, navigate, location.state]);

  const handleTermsAccept = async () => {
    try {
      // Update user profile to set terms acceptance to true
      await ApiService.updateUserProfile(currentUser.uid, {
        termsAndConditionsAccepted: true,
      }); // Hide terms modal on successful acceptance
      setShowTermsModal(false); // Get updated user profile and check credits
      const response = await ApiService.getUserProfile(currentUser.uid);
      const profileData = response.data || response;
      const credits = profileData.credits || 0;
      const payPerUse = profileData.payPerUseCredits || 0;
      setUserCredits(credits);
      setPayPerUseCredits(payPerUse);

      // Only redirect to purchase flow if user has absolutely no credits
      // Allow users with any credits to stay on dashboard and use "Purchase More Credits" button
      if (credits === 0 && payPerUse === 0) {
        navigate("/integrated-purchase-flow");
      }
      // If has any credits, stay on dashboard and show booking interface
    } catch (error) {
      console.error("Error accepting terms:", error);
      alert("Failed to save terms acceptance. Please try again.");
    }
  };

  const handleTermsCancel = () => {
    // If user cancels terms, logout and redirect to home
    handleLogout();
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Show loading while checking terms
  if (loading) {
    return (
      <div className="user-dashboard">
        <div className="loading-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-dashboard">
      {/* Show terms modal if user hasn't accepted terms */}
      {showTermsModal && (
        <TermsAcceptance
          onAccept={handleTermsAccept}
          onCancel={handleTermsCancel}
        />
      )}

      <header className="dashboard-header">
        <div className="header-content">
          <img src={logo} alt="Steam & Drive" className="dashboard-logo" />
          <div className="user-info">
            <span>
              Welcome, {currentUser?.displayName || currentUser?.email}
            </span>
            <button onClick={handleLogout} className="logout-btn">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-container">
        {" "}
        <div className="dashboard-content">
          {userCredits >= 1 || payPerUseCredits > 0 ? (
            <div className="booking-section">
              <div className="welcome-card">
                <div className="card-header">
                  <h2>Book Your Car Wash Service</h2>
                  <button
                    onClick={() => navigate("/booking-history")}
                    className="history-btn"
                  >
                    View Booking History
                  </button>
                </div>{" "}
                {/* Credits Display */}
                <div className="credits-display">
                  <div className="credits-info">
                    <div className="credit-item">
                      <span className="credit-label">Credits:</span>
                      <span className="credit-value">{userCredits}</span>
                    </div>
                    {payPerUseCredits > 0 && (
                      <div className="credit-item">
                        <span className="credit-label">
                          Pay-per-use Credits:
                        </span>
                        <span className="credit-value">{payPerUseCredits}</span>
                      </div>
                    )}{" "}
                  </div>{" "}
                  {/* Purchase Pay-per-use Credits Button - Only show if user has 0 pay-per-use credits */}
                  {payPerUseCredits === 0 && (
                    <div className="purchase-more-section">
                      <button
                        onClick={() =>
                          navigate("/integrated-purchase-flow", {
                            state: { payPerUseOnly: true },
                          })
                        }
                        className="purchase-more-btn"
                      >
                        Book Pay-per-use
                      </button>
                    </div>
                  )}
                </div>
                {/* Active Plans Display */}
                {activeSubscriptions.length > 0 && (
                  <div className="active-plans">
                    <h3>Active Plans</h3>
                    <div className="plans-list">
                      {activeSubscriptions.map((subscription) => (
                        <div key={subscription.id} className="plan-item">
                          <div className="plan-info">
                            <span className="plan-name">
                              {subscription.pricingPlan?.name}
                            </span>
                            <span className="plan-car-type">
                              for {subscription.carType?.name}
                            </span>
                          </div>
                          <div className="plan-validity">
                            {subscription.endDate
                              ? `Valid until ${new Date(
                                  subscription.endDate
                                ).toLocaleDateString()}`
                              : "Unlimited validity"}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <p>Select a date and time for your car wash service below.</p>
              </div>
              <BookingCalendar />
            </div>
          ) : (
            <div className="welcome-card">
              {location.state?.fromBookingConfirmation ? (
                <>
                  <h2>Booking Complete!</h2>
                  <p>
                    Your booking has been successfully confirmed. You currently
                    have no remaining credits.
                  </p>
                  <p>
                    To book another service, you can purchase more credits or
                    subscribe to a plan.
                  </p>
                  <button
                    onClick={() => navigate("/integrated-purchase-flow")}
                    className="purchase-credits-button"
                    style={{
                      background: "#4a9eff",
                      color: "white",
                      border: "none",
                      padding: "12px 24px",
                      borderRadius: "8px",
                      fontSize: "1rem",
                      fontWeight: "600",
                      cursor: "pointer",
                      marginTop: "16px",
                    }}
                  >
                    Purchase More Credits
                  </button>
                </>
              ) : (
                <>
                  <h2>Welcome to Your Dashboard!</h2>
                  <p>
                    This is your personal space where you can manage your car
                    wash services.
                  </p>
                  <p>More features coming soon...</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
